<template>
  <div
    class="w-full flex-col justify-start items-start gap-4 inline-flex relative"
  >
    <!-- Rank Floating -->
    <span
      v-if="rank"
      class="absolute top-[-10px] left-[-10px] text-white px-2 py-1 rounded w-8 h-8 z-20 flex items-center justify-center shadow-md text-lg font-semibold"
      :class="(rank === 1) ? 'bg-yellow-400' : (rank === 2) ? 'bg-orange-400' : (rank === 3) ? 'bg-stone-500' : 'bg-gray-400'"
    >
      {{ rank }}
    </span>
    <heart-icon
      :active="product?.wishes?.length > 0"
      class="right-[10px] top-[10px] absolute z-10"
      @change="(val)=>{ toggleWish(product, val) }"
    />
    <div
      class="image-container"
      style="width: 100%; padding-top: 133.33%; position: relative;"
    >
      <img
        class="absolute top-0 left-0 h-full self-stretch w-full object-cover cursor-pointer"
        :class="(isStockOut(product)) ? 'opacity-50' : 'opacity-100'"
        :src="product.thumb_image"
        @click="()=>{ router.push({name:routeName, params:{productId:product.it_id}}) }"
      >
      <ul class="absolute z-10 top-3 left-2 flex gap-2">
        <slot name="ribbon" />
        <li
          v-if="product?.has_coupon_discount"
          class="border-stone-600 font-normal leading-none bg-stone-700 text-white border rounded px-2 py-1"
        >
          {{ $t("쿠폰할인") }}
        </li>
        <li
          v-if="product?.default_price?.discountable?.rate"
          class="border-stone-600 font-normal leading-none bg-stone-900 text-white border rounded px-2 py-1"
        >
          {{ product.default_price.discountable.rate }}%
        </li>
        <li
          v-if="isStockOut(product)"
          class="border-stone-600 font-normal leading-none bg-stone-100 text-stone-500 border rounded px-2 py-1"
        >
          {{ $t("품절") }}
        </li>
      </ul>
    </div>
    <div class="self-stretch flex-col justify-center items-start gap-3 flex">
      <div class="text-stone-700 text-base font-normal leading-none">
        {{ __(product.it_name, product.it_name_en) }}
      </div>
      <div
        class="text-center text-stone-700 text-base font-normal font-['Pretendard'] leading-tight flex w-full items-center justify-between"
      >
        <product-price-info
          v-if="product.default_price.discountable?.before"
          class="lg:text-left lg:text-lg"
          :price="product.default_price.discountable?.before"
          :discount="product.default_price.discountable.rate"
          :discount-type="product.default_price.discountable.type"
          :fixed-discount-amount="product.default_price.discountable.amount"
        />
        <product-price-info
          v-else
          class="lg:text-left lg:text-lg"
          :price="product.default_price.value"
        />

        <span class="flex gap-0.5 text-stone-700 text-base font-semibold font-['Pretendard'] leading-none items-center">
          <cw-icon
            name="comment"
            class="w-[1rem]"
          />
          {{ product.reviews_count }}
        </span>
      </div>
      <!-- color_tip -->
      <div
        v-if="product.color_tips"
        class="flex flex-wrap items-center gap-1 absolute top-4 left-3 right-12 w-[10rem] max-w-[calc(100%-4rem)] max-h-[2.25rem] overflow-hidden"
      >
        <span
          v-for="(tip, tid) in product.color_tips"
          :key="tid"
          class="inline-block rounded-full w-4 h-4 cursor-pointer shadow hover:drop-shadow-lg"
          :style="{backgroundColor: tip.color_tip}"
          @click="changeImage(product, tip)"
        />
      </div>
      <div
        v-if="product?.icon_urls?.length > 0"
        class="flex gap-2 flex-wrap"
      >
        <img
          v-for="(url,idxx) in product.icon_urls"
          :key="idxx"
          :src="url"
          class="h-4 w-auto"
          :alt="idxx"
        >
      </div>
      <p
        v-if="expDateFormat(product.it_expdate)"
        class="text-cyan-700 font-bold"
      >
        {{ $t('{date}부터 출고됩니다.', {date: expDateFormat(product.it_expdate)}) }}
      </p>
      <div
        v-if="product.it_summary"
        class="flex gap-2 flex-wrap"
      >
        <div
          v-for="(tag, idxx) in product.it_summary.split(' ')"
          :key="idxx"
          class="bg-gray-100 text-sm py-1 px-2 rounded text-gray-600 cursor-pointer hover:bg-gray-50 "
          @click="changeKeyword(tag)"
        >
          {{ tag }}
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {useRouter} from "vue-router";
import {useWishList} from "@/utils/useWishList.js";
import {computed} from "vue";
import HeartIcon from "@/components/ui/icons/HeartIcon.vue";
import {$t, __} from "@/i18n/useLanguage.js";
import {$dialog} from "@/utils/pluginDialog.js";
import ProductPriceInfo from "@/routes/shop/ProductPriceInfo.vue";
import {expDateFormat} from "@/utils/DateHelper.js";

const router = useRouter();

const WishStore = useWishList();
const props = defineProps({
  product: {
    type: Object,
    default: () => {
      return {
        it_id: '',
        it_name: '',
        it_name_en: '',
        it_summary: '',
        thumb_image: '',
        default_price: {
          value: 0,
        },
        reviews_count: 0,
        color_tips: [],
      }
    }
  },
  routeName: {
    type: String,
    default: 'Product'
  },
  rank: {
    type: Number,
    default: null
  }
});

const product = computed(() => props.product);

const emits = defineEmits([
  'update:product',
])

function changeImage(product, tip) {
  product.thumb_image = tip.image_url;
}

function changeKeyword(tag) {
  // tag 에서 # 제거
  router.push({name: 'ProductSearch', query: {keyword: tag}})
}

async function toggleWish(product, val) {
  const temp = {...product};
  try {
    if (val) {
      await $dialog($t('위시리스트에 추가할까요?'), {
        title: $t('위시리스트 추가'),
      });
      await WishStore.store({
        it_id: temp.it_id
      })
      if (!temp.wishes) {
        temp.wishes = [];
      }
      temp.wishes.push(WishStore.getModel);
    } else {
      await WishStore.destroy(temp.wishes[0], $t('위시리스트에서 제외할까요?'));
      temp.wishes = [];
    }
    emits('update:product', temp);
  } catch (e) {
    // empty
  }
}

function isStockOut(item) {
  const qty = item?.default_price?.model?.op_qty;
  return typeof qty === 'number' && qty < 1;
}

</script>
